import React, { lazy, Suspense, useMemo } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { AppRoute } from "@app/models/AppRoute";
import { useGetUserPermissions } from "@user/hooks";
import { Permission } from "@user/models";
import ReportingDrawer from "@reporting/components/ReportingDrawer";
import AppLoadingIndicator from "@app/components/AppLoadingIndicator";
import { getReportingNavigationLinks } from "@reporting/utils/getReportingNavigationLinks";
import { useFundsDataValidation } from "@reporting/hooks/useFundsDataValidation";
import { useAlpInvestEntitleDataValidaion } from "@reporting/hooks/useAlpInvestEntitleDataValidaion";
import { useAppSelector } from "@app/hooks";
import { entitledFundsSelector, fundsSelector } from "@store/Entities/selectors";

const AccountSummaryAlpInvestPage = lazy(
  () => import("@accountSummaryAlpInvest/containers/AccountSummaryAlpInvestPage")
);
const AccountSummaryCarlylePage = lazy(() => import("@accountSummaryCarlyle/containers/AccountSummaryCarlylePage"));
const CapitalAccountStatementPage = lazy(
  () => import("@capitalAccountStatement/containers/CapitalAccountStatementPage")
);
const ScheduleOfInvestmentsPage = lazy(() => import("@scheduleOfInvestments/containers/ScheduleOfInvestmentsPage"));
const CashFlowActivityPage = lazy(() => import("@cashFlowActivity/containers/CashFlowActivityPage"));
const PortfolioPerformancePage = lazy(() => import("@portfolioPerformance/containers/PortfolioPerformancePage"));
const DiversificationAnalysisPage = lazy(
  () => import("@diversificationAnalysis/containers/DiversificationAnalysisPage")
);

const ReportingRouter = () => {
  const location = useLocation();
  const permissions = useGetUserPermissions();
  const fundAvailable = useFundsDataValidation();
  const isAlpInvestData = useAlpInvestEntitleDataValidaion();

  const entitledFunds = useAppSelector(entitledFundsSelector);
  const funds = useAppSelector(fundsSelector);

  // Reporting routes/redirects depend on entitled funds + reporting catalog. On a hard reload
  // these are still loading, so we must wait before evaluating routes — otherwise a valid Carlyle
  // deep link briefly fails the fundAvailable check and falls through to the AlpInvest fallback.
  const isEntitlementsLoading =
    entitledFunds.loading ||
    (!entitledFunds.data && !entitledFunds.error) ||
    funds.loading ||
    (!funds.data && !funds.error);

  const routeToRedirect = useMemo(() => {
    switch (true) {
      case permissions[Permission.ViewReportsCarlyle] && fundAvailable:
        return AppRoute.CapitalAccountStatement;
      case permissions[Permission.ViewPortfolioPerformanceReport]:
        return AppRoute.PortfolioPerformance;
      case permissions[Permission.ViewDiversificationAnalysisReport]:
        return AppRoute.DiversificationAnalysis;
      case permissions[Permission.ViewAccountSummaryCarlyle] && fundAvailable:
        return AppRoute.AccountSummaryCarlyle;
      case permissions[Permission.ViewAccountSummaryAlpInvest]:
        return AppRoute.AccountSummaryAlpInvest;
      default:
        return AppRoute.Home;
    }
  }, [permissions, fundAvailable]);

  const sideBarRoutes = useMemo(
    () => getReportingNavigationLinks(permissions, fundAvailable, isAlpInvestData),
    [permissions, fundAvailable, isAlpInvestData]
  );

  if (isEntitlementsLoading) {
    return <AppLoadingIndicator />;
  }

  return (
    <ReportingDrawer activeRoute={location.pathname as AppRoute} routes={sideBarRoutes}>
      <Suspense fallback={<AppLoadingIndicator />}>
        <Switch>
          <Route exact path={AppRoute.ReportingBase}>
            <Redirect to={routeToRedirect} />
          </Route>
          {permissions[Permission.ViewReportsCarlyle] && fundAvailable && (
            <Route path={AppRoute.AccountSummaryCarlyle} component={AccountSummaryCarlylePage} />
          )}
          {permissions[Permission.ViewReportsCarlyle] &&
            fundAvailable && [
              <Route
                key={AppRoute.CapitalAccountStatement}
                path={AppRoute.CapitalAccountStatement}
                component={CapitalAccountStatementPage}
              />,
              <Route
                key={AppRoute.ScheduleOfInvestments}
                path={AppRoute.ScheduleOfInvestments}
                component={ScheduleOfInvestmentsPage}
              />,
              <Route
                key={AppRoute.CashFlowActivity}
                path={AppRoute.CashFlowActivity}
                component={CashFlowActivityPage}
              />,
            ]}
          {permissions[Permission.ViewAccountSummaryAlpInvest] && (
            <Route path={AppRoute.AccountSummaryAlpInvest} component={AccountSummaryAlpInvestPage} />
          )}
          {permissions[Permission.ViewPortfolioPerformanceReport] && (
            <Route path={AppRoute.PortfolioPerformance} component={PortfolioPerformancePage} />
          )}
          {permissions[Permission.ViewDiversificationAnalysisReport] && (
            <Route path={AppRoute.DiversificationAnalysis} component={DiversificationAnalysisPage} />
          )}
          <Route path="**">
            <Redirect to={AppRoute.Home} />
          </Route>
        </Switch>
      </Suspense>
    </ReportingDrawer>
  );
};

export default ReportingRouter;
